"use client";

import * as React from "react";
import { cn } from "lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

type PaginationProps = {
  totalEntries: number;
  entriesPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  showSummary?: boolean;
  maxVisiblePages?: number;
  className?: string;
  onEntriesPerPageChange?: (entriesPerPage: number) => void;
  entriesPerPageOptions?: number[];
};

export default function Pagination({
  totalEntries,
  entriesPerPage,
  currentPage,
  onPageChange,
  showSummary = false,
  maxVisiblePages = 5,
  className,
  onEntriesPerPageChange,
  entriesPerPageOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalEntries / Math.max(1, entriesPerPage)));

  const getPageNumbers = React.useCallback((): (number | "...")[] => {
    // Show all if small enough
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const middle = Math.floor(maxVisiblePages / 2);

    // Near the start
    if (currentPage <= middle + 1) {
      return [
        ...Array.from({ length: maxVisiblePages }, (_, i) => i + 1),
        "...",
        totalPages,
      ];
    }

    // Near the end
    if (currentPage >= totalPages - middle) {
      return [
        1,
        "...",
        ...Array.from(
          { length: maxVisiblePages },
          (_, i) => totalPages - maxVisiblePages + 1 + i
        ),
      ];
    }

    // In the middle
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  }, [currentPage, totalPages, maxVisiblePages]);

  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= totalPages;

  const handlePrev = () => {
    if (!isPrevDisabled) onPageChange(currentPage - 1);
  };
  const handleNext = () => {
    if (!isNextDisabled) onPageChange(currentPage + 1);
  };

  const from = (currentPage - 1) * entriesPerPage + 1;
  const to = Math.min(currentPage * entriesPerPage, totalEntries);

  const pageNumbers = getPageNumbers();

  return (
    <div
      className={cn(
        "w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4",
        "px-4 py-3 mt-4 bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl shadow-sm",
        className
      )}
      role="navigation"
      aria-label="Pagination"
    >
      {/* Left side: Summary and entries per page dropdown */}
      <div className="flex items-center gap-4">
        {showSummary && (
          <p className="text-[13px] font-medium text-content-muted dark:text-gray-400">
            Showing <span className="text-content dark:text-white font-semibold">{totalEntries === 0 ? 0 : from}&ndash;{to}</span> of <span className="text-content dark:text-white font-semibold">{totalEntries}</span> entries
          </p>
        )}

        {onEntriesPerPageChange && (
          <select
            value={entriesPerPage}
            onChange={(e) => onEntriesPerPageChange(Number(e.target.value))}
            className="h-8 px-3 py-1 text-[13px] border border-line dark:border-dark-600 rounded-lg bg-surface dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-colors"
            aria-label="Entries per page"
          >
            {entriesPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Right side: Pagination controls */}
      <div className="flex items-center gap-1.5">
        {/* Previous Button */}
        <button
          type="button"
          onClick={handlePrev}
          disabled={isPrevDisabled}
          aria-label="Go to previous page"
          className={cn(
            "h-8 px-3 rounded-lg text-[13px] font-medium transition-all flex items-center gap-1.5",
            "border border-line dark:border-dark-600",
            "bg-surface dark:bg-dark-700 text-content-secondary dark:text-gray-300",
            "hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500",
            isPrevDisabled && "opacity-40 cursor-not-allowed pointer-events-none"
          )}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page, index) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="h-8 w-8 flex items-center justify-center text-[13px] text-content-subtle dark:text-gray-500"
                >
                  ...
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "h-8 w-8 rounded-lg text-[13px] font-semibold transition-all",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800",
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm shadow-blue-500/25"
                    : "bg-surface dark:bg-dark-700 border border-line dark:border-dark-600 text-content-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500"
                )}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          aria-label="Go to next page"
          className={cn(
            "h-8 px-3 rounded-lg text-[13px] font-medium transition-all flex items-center gap-1.5",
            "border border-line dark:border-dark-600",
            "bg-surface dark:bg-dark-700 text-content-secondary dark:text-gray-300",
            "hover:bg-gray-50 dark:hover:bg-dark-600 hover:border-line-strong dark:hover:border-dark-500",
            isNextDisabled && "opacity-40 cursor-not-allowed pointer-events-none"
          )}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
